/**
 * Platform theme catalog — locked set of 11.
 *
 * Each theme defines a primary (sidebar/top-nav bg) and a secondary
 * (buttons, accents). Optional per-theme overrides customise the shell
 * behaviour when auto-derived defaults aren't enough for a specific look.
 *
 * Fields:
 *   primary                     sidebar + top-nav background
 *   secondary                   button/badge/accent colour
 *   shellText                   explicit shell text colour (else auto via perceived brightness)
 *   shellTextInactiveOpacity    inactive nav opacity (default 0.65)
 *   sectionLabelColor           section-header colour (default = secondary)
 *   sectionLabelOpacity         section-header opacity (default 1.0)
 *   activeBorderWidth           active nav left-border width px (default 3)
 *   activeIconColor             override icon colour for active items (e.g. Sanctuary yellow)
 *   onSecondary                 explicit text colour for buttons w/ secondary bg
 *   breadcrumbWeight            page-title font weight (default 500)
 *
 * Status badges (Published / Draft / Error / Warning) are INTENTIONALLY
 * decoupled from these palettes so the drafting workflow stays readable
 * across every theme.
 *
 * NOTE: The top-nav search bar glass effect (bg/border/placeholder colour)
 * is derived from the resolved `shellText` colour polarity — not stored
 * per-theme — so every light-shell theme (Monocle) picks the dark-glass
 * variant and every dark-shell theme picks the light-glass variant.
 */

export const THEMES = [
  {
    id: "default",
    name: "Default",
    primary: "#040B1D",
    secondary: "#10B7C0",
    contentIconOn: "primary",     // icon=primary, chip bg=secondary
  },
  {
    id: "sunset",
    name: "Sunset",
    primary: "#510d66",
    secondary: "#FF8200",
    shellText: "#FFFFFF",
    contentIconOn: "secondary",   // icon=orange, chip bg=deep plum
  },
  {
    id: "academic_elite",
    name: "Academic Elite",
    primary: "#003366",
    secondary: "#C5B358",
    contentIconOn: "secondary",   // icon=gold, chip bg=navy
  },
  {
    id: "pulse",
    name: "Pulse",
    primary: "#aa2121",
    secondary: "#BDBDBD",
    shellText: "#FFFFFF",
    shellTextInactiveOpacity: 0.75,
    breadcrumbWeight: 700,
    contentIconOn: "secondary",   // icon=gray, chip bg=red
  },
  {
    id: "vitality",
    name: "Vitality",
    primary: "#18501a",
    secondary: "#A5D6A7",
    shellText: "#FFFFFF",
    activeBorderWidth: 4,
    onSecondary: "#000000",
    breadcrumbWeight: 600,
    contentIconOn: "primary",     // icon=forest, chip bg=mint
  },
  {
    id: "specialist",
    name: "Specialist",
    primary: "#005789",
    secondary: "#E1F5FE",
    sectionLabelOpacity: 0.8,
    onSecondary: "#1A1A1A",
    breadcrumbWeight: 600,
    contentIconOn: "primary",     // icon=deep blue, chip bg=sky
  },
  {
    id: "innovator",
    name: "Innovator",
    primary: "#082FA8",
    secondary: "#51F5E3",
    onSecondary: "#040B1D",
    breadcrumbWeight: 600,
    contentIconOn: "primary",     // icon=navy, chip bg=mint-cyan
  },
  {
    id: "monocle",
    name: "Monocle",
    primary: "#FBC02D",
    secondary: "#3E2723",
    contentIconOn: "primary",     // icon=yellow, chip bg=dark brown
  },
  {
    id: "sanctuary",
    name: "Sanctuary",
    primary: "#134E4A",
    secondary: "#FDE68A",
    sectionLabelOpacity: 0.9,
    activeIconColor: "#FDE68A",
    onSecondary: "#134E4A",
    breadcrumbWeight: 600,
    contentIconOn: "primary",     // icon=teal, chip bg=yellow
  },
  {
    id: "pavillion",
    name: "Pavillion",
    primary: "#475569",
    secondary: "#94A3B8",
    contentIconOn: "secondary",   // icon=light slate, chip bg=deep slate
  },
  {
    id: "the_nexus",
    name: "The Nexus",
    primary: "#1E1B4B",
    secondary: "#818CF8",
    contentIconOn: "primary",     // icon=indigo, chip bg=periwinkle
  },
];

export function getTheme(id) {
  return THEMES.find((t) => t.id === id) || THEMES[0];
}
